import React, { useEffect, useState } from 'react';
import {
  Table, Input, Button, Select, Space, Tag,
  message, Modal, Form, Rate, DatePicker,
  Descriptions,
  Row,
  Col
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';
import { dateFormat } from '../../Utils/formatDate';
import moment from 'moment';

const { Option } = Select;

const LoanRequestList = () => {
  const token = useSelector((state) => state.user.token);
  const user = useSelector((state) => state.user.user);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sorter, setSorter] = useState({ field: 'loan_request_id', order: 'descend' });

  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [feedbackForm] = Form.useForm();
  const [loanForm] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [vehicles, setVehicles] = useState([]);


  const fetchData = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const res = await apiCall({
        method: 'GET',
        url: `loan-requests?page=${page}&limit=${limit}&search=${search}&sortBy=${sorter.field}&order=${sorter.order === 'ascend' ? 'asc' : 'desc'}`,
        data: {
          headers: { Authorization: 'Bearer ' + token },
        },
      });
      if (res.status === 'success') {
        setData(res.data.data);
        setMeta({
          page: res.data.page,
          limit: res.data.limit,
          total: res.data.total_data,
        });
      } else {
        message.error(res.message);
      }
    } catch {
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(meta.page, meta.limit);
  }, [search, sorter, meta.page, meta.limit]);

  const handleTableChange = (pagination, filters, sorterObj) => {
    const sort = {
      field: sorterObj.field || 'loan_request_id',
      order: sorterObj.order || 'descend',
    };
    setSorter(sort);
    setMeta({
      page: pagination.current,
      limit: pagination.pageSize,
      total: meta.total,
    });
  };


  const openFeedbackModal = (record) => {
    setSelectedLoan(record);
    setFeedbackVisible(true);
    feedbackForm.resetFields();
  };

  const openFormModal = async () => {
    setSelectedLoan(null);
    loanForm.resetFields();
    try {
      const res = await apiCall({
        method: 'GET',
        url: '/vehicle-available',
        data: {
          headers: { Authorization: 'Bearer ' + token },
        },
      });
      if (res.status === 'success') {
        const vehicleOptions = res.data.map((v) => ({
          label: `${v.license_plate} - ${v.brand} ${v.model}`,
          value: v.vehicle_id,
        }));
        setVehicles(vehicleOptions);
      } else {
        message.error(res.message);
      }
    } catch {
      message.error('Failed to load vehicles');
    }
    setFormVisible(true);
  };


  const submitFeedback = async () => {
    const values = await feedbackForm.validateFields();
    try {
      const res = await apiCall({
        method: 'POST',
        url: '/loan-feedback',
        data: {
          data: {
            ...values,
            loan_request_id: selectedLoan.loan_request_id,
          },
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
        },
      });
      if (res.status === 'success') {
        message.success('Feedback submitted successfully');
        setFeedbackVisible(false);
        fetchData(meta.page, meta.limit);
      } else {
        message.error(res.message);
      }
    } catch {
      message.error('Failed to submit feedback');
    }
  };

  const submitLoanRequest = async () => {
    try {
      const values = await loanForm.validateFields();
      const formattedValues = {
        ...values,
        start_date: values.start_date.format('YYYY-MM-DD'),
        end_date: values.end_date.format('YYYY-MM-DD'),
      };

      const res = await apiCall({
        method: 'POST',
        url: '/loan-requests',
        data: {
          data: formattedValues,
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
          },
        },
      });
      if (res.status === 'success') {
        message.success('Loan request submitted');
        setFormVisible(false);
        loanForm.resetFields(); // reset hanya setelah sukses
        fetchData(meta.page, meta.limit);
      } else {
        message.error(res.message);
      }
    } catch {
      message.error('Failed to submit loan request');
    }
  };

  const openDetailModal = (record) => {
    setSelectedLoan(record);
    setDetailVisible(true);
  };

  const columns = [
    {
      title: 'No',
      render: (_, __, index) => (meta.page - 1) * meta.limit + index + 1,
    },
    {
      title: 'User Request',
      dataIndex: 'users',
      render: (_, record) => record.user_request.name,
    },
    {
      title: 'Vehicle',
      dataIndex: 'vechicle',
      render: (_, record) => record.vehicle !== null ? record.vehicle.license_plate + " " + record.vehicle.brand : null,
    },
    {
      title: 'Request Date',
      dataIndex: 'request_date',
      sorter: true,
      render: (_, record) => dateFormat(record.request_date),
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'approval_status',
      sorter: true,
      render: (status) => <Tag color={status === 'Approved' ? 'green' : status === 'Pending' ? 'orange' : 'red'}>{status}</Tag>,
    },
    {
      title: 'Feedback',
      dataIndex: 'loan_feedback',
      render: (feedback) => feedback ? <Tag color="blue">Completed</Tag> : <Tag color="orange">Serve</Tag>,
    },
    {
      title: 'Actions',
      render: (_, record) => {
        const isApproved = record.approval_status === 'Approved';
        const isDone = record.loan_feedback !== null;
        return (
          <Space>
            {!isDone && isApproved && record.users_id === JSON.parse(user).users_id && (
              <Button type="primary" size="small" onClick={() => openFeedbackModal(record)}>Complete</Button>
            )}
            <Button size="small" onClick={() => openDetailModal(record)}>Detail</Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <h2>Loan Requests</h2>
      <Row style={{ marginBottom: 10 }}>
        <Col span={8}>
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={16} style={{ display: 'flex', justifyContent: 'flex-end' }} >
          <Button type="primary" onClick={openFormModal}>New Request</Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="loan_request_id"
        loading={loading}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title="Loan Feedback"
        open={feedbackVisible}
        onCancel={() => setFeedbackVisible(false)}
        onOk={submitFeedback}
        okText="Submit"
      >
        <Form layout="vertical" form={feedbackForm}>
          <Form.Item name="satisfaction" label="Satisfaction" rules={[{ required: true }]}>
            <Select>
              <Option value="Satisfied">Satisfied</Option>
              <Option value="Not Satisfied">Not Satisfied</Option>
            </Select>
          </Form.Item>
          <Form.Item name="rating" label="Rating" rules={[{ required: true }]}><Rate /></Form.Item>
          <Form.Item name="comments" label="Comments"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title="New Loan Request"
        open={formVisible}
        onCancel={() => setFormVisible(false)}
        onOk={submitLoanRequest}
        okText="Submit"
      >
        <Form layout="vertical" form={loanForm}>
          <Form.Item name="vehicle_id" label="Vehicle" rules={[{ required: true }]}>
            <Select
              placeholder="Select a vehicle"
              showSearch
              options={vehicles}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="end_date" label="End Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="purpose" label="Purpose" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Loan Request Detail"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {selectedLoan && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Request ID">{selectedLoan.loan_request_id}</Descriptions.Item>
            <Descriptions.Item label="Request Date">{dateFormat(selectedLoan.request_date)}</Descriptions.Item>
            <Descriptions.Item label="Start Date">{moment(selectedLoan.start_date).format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label="End Date">{moment(selectedLoan.end_date).format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label="Purpose">{selectedLoan.purpose}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={
                selectedLoan.approval_status === 'Approved' ? 'green' :
                  selectedLoan.approval_status === 'Pending' ? 'orange' :
                    'red'
              }>
                {selectedLoan.approval_status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Reason">{selectedLoan.reason}</Descriptions.Item>
            {selectedLoan.vehicle && (
              <>
                <Descriptions.Item label="Vehicle">
                  {selectedLoan.vehicle.license_plate} - {selectedLoan.vehicle.brand} {selectedLoan.vehicle.model}
                </Descriptions.Item>
                <Descriptions.Item label="Color">{selectedLoan.vehicle.color}</Descriptions.Item>
              </>
            )}
            {selectedLoan.loan_feedback && (
              <>
                <Descriptions.Item label="Feedback - Satisfaction">{selectedLoan.loan_feedback.satisfaction}</Descriptions.Item>
                <Descriptions.Item label="Feedback - Rating">
                  <Rate disabled defaultValue={selectedLoan.loan_feedback.rating} />
                </Descriptions.Item>
                <Descriptions.Item label="Feedback - Comments">{selectedLoan.loan_feedback.comments}</Descriptions.Item>
              </>
            )}
          </Descriptions>
        )}
      </Modal>


    </div>
  );
};

export default LoanRequestList;
